import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { queryKeys, useApiMutation, useApiQuery } from '../../api/hooks';
import type {
  ClinicalContextResponse,
  ConsentTemplateResponse,
  ContraindicationCode,
  Fitzpatrick,
  IntakeRequest,
  IntakeResponse,
  PatchTestResponse,
  PhotoLinkResponse,
  PhotoSetResponse,
  RecordPatchTestRequest,
  ServiceResponse,
  SignConsentRequest,
} from '../../api/types';
import { useApiClient } from '../../api/apiContext';
import { useAuthStore } from '../../auth/authStore';
import { ErrorState } from '../../components/states/StateViews';
import { clinicDay, clinicTime } from '../scheduling/clinicTime';
import { SignaturePad } from './SignaturePad';

/**
 * The clinical tab on a patient's profile (FR-501…506, FR-703, RULE-003, RULE-010, RULE-012).
 *
 * Rendered only for a viewer holding `CLINICAL_RECORD_READ`, which reception does not and never
 * gains — but the permission check here is a courtesy to the reader, not the boundary. The boundary
 * is the API, which refuses the call; hiding a tab is not a security control and this file should
 * not be read as if it were one.
 *
 * Everything is on one panel rather than behind sub-tabs. A doctor with a patient in the chair
 * wants the allergy, the patch test and the consent visible at once — the moment any of them is a
 * click away is the moment somebody proceeds without having looked.
 */
export function ClinicalPanel({
  clinicId,
  membershipId,
  patientName,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly patientName: string;
}) {
  const { t } = useTranslation();
  const permissions = useAuthStore((state) => state.permissions);
  const canWrite = permissions.has('CLINICAL_RECORD_WRITE');
  const canSeePhotos = permissions.has('CLINICAL_PHOTO_READ');

  const [serviceId, setServiceId] = useState('');
  const [editingIntake, setEditingIntake] = useState(false);
  const [signing, setSigning] = useState<ConsentTemplateResponse | null>(null);

  const services = useApiQuery<ServiceResponse[]>(
    queryKeys.services(clinicId),
    `/clinics/${clinicId}/catalog/services`,
  );

  const context = useApiQuery<ClinicalContextResponse>(
    queryKeys.clinicalContext(clinicId, membershipId, serviceId),
    `/clinics/${clinicId}/clinical/patients/${membershipId}${serviceId ? `?serviceId=${serviceId}` : ''}`,
  );

  if (context.error) {
    return <ErrorState error={context.error} />;
  }

  const data = context.data;
  const intake = data?.intake ?? null;

  return (
    <div className="clinical">
      <section className="section">
        <h3 className="section__title">{t('clinical.safety')}</h3>
        <label className="field">
          <span className="field__label">{t('clinical.forTreatment')}</span>
          <select
            className="field__input"
            value={serviceId}
            onChange={(event) => setServiceId(event.target.value)}
          >
            <option value="">{t('clinical.anyTreatment')}</option>
            {(services.data ?? []).map((service) => (
              <option key={service.id} value={service.id}>
                {service.nameEn ?? service.nameAr}
              </option>
            ))}
          </select>
        </label>
        <SafetyFindings findings={data?.gate?.findings ?? []} clear={data?.gate?.clear ?? true} />
      </section>

      <section className="section">
        <div className="section__heading">
          <h3 className="section__title">{t('clinical.intake')}</h3>
          {canWrite ? (
            <button
              type="button"
              className="button button--ghost button--compact"
              onClick={() => setEditingIntake((open) => !open)}
            >
              {editingIntake ? t('common.cancel') : intake ? t('clinical.amendIntake') : t('clinical.takeIntake')}
            </button>
          ) : null}
        </div>

        {editingIntake ? (
          <IntakeForm
            clinicId={clinicId}
            membershipId={membershipId}
            existing={intake}
            onSaved={() => setEditingIntake(false)}
          />
        ) : (
          <IntakeSummary intake={intake} />
        )}
      </section>

      <section className="section">
        <div className="section__heading">
          <h3 className="section__title">{t('clinical.patchTests')}</h3>
        </div>
        <PatchTests
          clinicId={clinicId}
          membershipId={membershipId}
          tests={data?.patchTests ?? []}
          services={services.data ?? []}
          canWrite={canWrite}
        />
      </section>

      <section className="section">
        <h3 className="section__title">{t('clinical.consents')}</h3>
        <Consents
          clinicId={clinicId}
          membershipId={membershipId}
          signatures={data?.consents ?? []}
          outstanding={data?.outstandingConsents ?? []}
          canWrite={canWrite}
          onSign={setSigning}
        />
      </section>

      {canSeePhotos ? (
        <section className="section">
          <h3 className="section__title">{t('clinical.photos')}</h3>
          <PhotoSets
            clinicId={clinicId}
            membershipId={membershipId}
            sets={data?.photoSets ?? []}
            canWrite={permissions.has('CLINICAL_PHOTO_WRITE')}
          />
        </section>
      ) : null}

      <section className="section">
        <h3 className="section__title">{t('clinical.visits')}</h3>
        {(data?.recentSessions ?? []).length === 0 ? (
          <p className="field__hint">{t('clinical.noVisits')}</p>
        ) : (
          <ul className="stack">
            {(data?.recentSessions ?? []).map((session) => (
              <li key={session.id} className="callout">
                <strong>{session.serviceNameEn ?? session.serviceNameAr}</strong>{' '}
                <span className="ltr">{session.recordedAt ? clinicDay(session.recordedAt) : ''}</span>
                {session.complicationFlagged ? (
                  <span className="badge badge--danger">{t('clinical.complication')}</span>
                ) : null}
                {session.note ? <p>{session.note}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {signing ? (
        <SignConsent
          clinicId={clinicId}
          membershipId={membershipId}
          patientName={patientName}
          template={signing}
          onClose={() => setSigning(null)}
        />
      ) : null}
    </div>
  );
}

// ── The gate (FR-503, RULE-010) ───────────────────────────────────────────

export function SafetyFindings({
  findings,
  clear,
}: {
  readonly findings: NonNullable<ClinicalContextResponse['gate']>['findings'];
  readonly clear: boolean;
}) {
  const { t } = useTranslation();

  if (clear || !findings || findings.length === 0) {
    return <p className="field__hint">{t('clinical.gateClear')}</p>;
  }

  return (
    <ul className="stack">
      {findings.map((finding) => (
        <li
          key={finding.code}
          className={finding.blocking ? 'callout callout--danger' : 'callout callout--warning'}
        >
          {/* Localised from the code, never from the server's sentence: the API's message is for a
              developer reading a log, and the clinic reads Arabic. */}
          <strong>{t(`clinical.findings.${finding.code}`, { defaultValue: finding.message ?? '' })}</strong>
          {finding.detail ? <span className="field__hint"> · {finding.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}

// ── Intake (FR-501) ───────────────────────────────────────────────────────

const FITZPATRICK: readonly Fitzpatrick[] = ['I', 'II', 'III', 'IV', 'V', 'VI'];

const CONTRAINDICATIONS: readonly ContraindicationCode[] = [
  'PREGNANT_OR_NURSING',
  'PHOTOSENSITISING_MEDICATION',
  'RECENT_ISOTRETINOIN',
  'ACTIVE_SKIN_INFECTION',
  'SUSPICIOUS_LESION',
  'IMPLANTED_ELECTRONIC_DEVICE',
  'PREVIOUS_ADVERSE_REACTION',
  'KELOID_SCARRING',
  'ACTIVE_SKIN_CONDITION',
  'RECENT_SUN_EXPOSURE',
  'ANTICOAGULANT_THERAPY',
  'HORMONAL_DISORDER',
  'DIABETES',
  'EPILEPSY',
  'RELEVANT_ALLERGY',
  'OTHER',
];

function IntakeSummary({ intake }: { readonly intake: IntakeResponse | null }) {
  const { t } = useTranslation();

  if (!intake) {
    return <p className="field__hint">{t('clinical.noIntake')}</p>;
  }

  return (
    <>
      {intake.stale ? <p className="callout callout--warning">{t('clinical.intakeStale')}</p> : null}
      <dl className="detail-list">
        <div>
          <dt>{t('clinical.fitzpatrick')}</dt>
          <dd>{intake.fitzpatrick ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('clinical.allergies')}</dt>
          <dd>{intake.allergies ?? (intake.allergiesReviewed ? t('clinical.noneKnown') : '—')}</dd>
        </div>
        <div>
          <dt>{t('clinical.medications')}</dt>
          <dd>{intake.medications ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('clinical.skinConditions')}</dt>
          <dd>{intake.skinConditions ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('clinical.notes')}</dt>
          <dd>{intake.notes ?? '—'}</dd>
        </div>
        <div>
          <dt>{t('clinical.version')}</dt>
          <dd className="ltr">
            {intake.version} · {intake.recordedAt ? clinicDay(intake.recordedAt) : ''}
          </dd>
        </div>
      </dl>
      {(intake.contraindications ?? []).length > 0 ? (
        <ul className="stack">
          {(intake.contraindications ?? []).map((flag) => (
            <li key={flag.code} className={flag.absolute ? 'callout callout--danger' : 'callout'}>
              {t(`clinical.contraindications.${flag.code}`)}
              {flag.note ? <span className="field__hint"> · {flag.note}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

function IntakeForm({
  clinicId,
  membershipId,
  existing,
  onSaved,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly existing: IntakeResponse | null;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  // Pre-filled from the live version, because an amendment is a correction to what she said, not a
  // fresh interrogation — and a form that emptied itself would lose the answers nobody re-asked.
  const [fitzpatrick, setFitzpatrick] = useState<string>(existing?.fitzpatrick ?? '');
  const [allergies, setAllergies] = useState(existing?.allergies ?? '');
  const [medications, setMedications] = useState(existing?.medications ?? '');
  const [skinConditions, setSkinConditions] = useState(existing?.skinConditions ?? '');
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [reviewed, setReviewed] = useState(existing?.allergiesReviewed ?? false);
  const [flags, setFlags] = useState<Set<string>>(
    new Set((existing?.contraindications ?? []).map((flag) => flag.code as string)),
  );

  const save = useApiMutation<unknown, IntakeRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/clinical/patients/${membershipId}/intake`,
      options: { method: 'PUT', body },
    }),
    {
      invalidates: [['clinics', clinicId, 'clinical', membershipId]],
      onSuccess: onSaved,
    },
  );

  return (
    <form
      className="stack"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate({
          fitzpatrick: (fitzpatrick || undefined) as IntakeRequest['fitzpatrick'],
          allergies: allergies.trim() || undefined,
          medications: medications.trim() || undefined,
          skinConditions: skinConditions.trim() || undefined,
          notes: notes.trim() || undefined,
          allergiesReviewed: reviewed,
          contraindications: [...flags].map((code) => ({ code: code as ContraindicationCode })),
          source: 'STAFF',
        });
      }}
    >
      <div className="form-grid">
        <label className="field">
          <span className="field__label">{t('clinical.fitzpatrick')}</span>
          <select
            className="field__input"
            value={fitzpatrick}
            onChange={(event) => setFitzpatrick(event.target.value)}
          >
            <option value="">—</option>
            {FITZPATRICK.map((type) => (
              <option key={type} value={type}>
                {type} · {t(`clinical.fitzpatrickTypes.${type}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">{t('clinical.allergies')}</span>
          <input
            className="field__input"
            value={allergies}
            onChange={(event) => setAllergies(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('clinical.medications')}</span>
          <input
            className="field__input"
            value={medications}
            onChange={(event) => setMedications(event.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">{t('clinical.skinConditions')}</span>
          <input
            className="field__input"
            value={skinConditions}
            onChange={(event) => setSkinConditions(event.target.value)}
          />
        </label>
      </div>

      <label className="field">
        <span className="field__label">{t('clinical.notes')}</span>
        <textarea
          className="field__input"
          rows={3}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      <label className="field field--inline">
        <input type="checkbox" checked={reviewed} onChange={(event) => setReviewed(event.target.checked)} />
        <span>{t('clinical.allergiesReviewed')}</span>
      </label>

      <fieldset className="fieldset">
        <legend>{t('clinical.checklist')}</legend>
        <div className="checkbox-grid">
          {CONTRAINDICATIONS.map((code) => (
            <label key={code} className="field field--inline">
              <input
                type="checkbox"
                checked={flags.has(code)}
                onChange={(event) => {
                  const next = new Set(flags);
                  if (event.target.checked) {
                    next.add(code);
                  } else {
                    next.delete(code);
                  }
                  setFlags(next);
                }}
              />
              <span>{t(`clinical.contraindications.${code}`)}</span>
            </label>
          ))}
        </div>
      </fieldset>

      {save.error ? <ErrorState error={save.error} /> : null}

      <p className="field__hint">{t('clinical.intakeVersionHint')}</p>
      <div className="table__actions">
        <button type="submit" className="button button--primary" disabled={save.isPending}>
          {t('common.save')}
        </button>
      </div>
    </form>
  );
}

// ── Patch tests (FR-502) ──────────────────────────────────────────────────

function PatchTests({
  clinicId,
  membershipId,
  tests,
  services,
  canWrite,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly tests: readonly PatchTestResponse[];
  readonly services: readonly ServiceResponse[];
  readonly canWrite: boolean;
}) {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [serviceId, setServiceId] = useState('');

  const invalidates = [['clinics', clinicId, 'clinical', membershipId]];

  const record = useApiMutation<unknown, RecordPatchTestRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/clinical/patients/${membershipId}/patch-tests`,
      options: { method: 'POST', body },
    }),
    { invalidates, onSuccess: () => setRecording(false) },
  );

  const read = useApiMutation<unknown, { readonly id: string; readonly result: string }>(
    ({ id, result }) => ({
      path: `/clinics/${clinicId}/clinical/patch-tests/${id}/reading`,
      options: { method: 'POST', body: { result } },
    }),
    { invalidates },
  );

  return (
    <>
      {tests.length === 0 ? (
        <p className="field__hint">{t('clinical.noPatchTests')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('clinical.tested')}</th>
                <th scope="col">{t('booking.service')}</th>
                <th scope="col">{t('common.status')}</th>
                <th scope="col">{t('clinical.validUntil')}</th>
                <th scope="col">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {tests.map((test) => (
                <tr key={test.id}>
                  <td className="ltr">
                    {test.testedAt?.slice(0, 10)} {clinicTime(test.testedAt)}
                  </td>
                  <td>{test.serviceNameEn ?? test.serviceNameAr ?? t('clinical.anyTreatment')}</td>
                  <td>
                    {t(`clinical.patchTestResults.${test.result ?? 'PENDING'}`)}
                    {test.currentlyValid ? <span className="badge">{t('clinical.valid')}</span> : null}
                  </td>
                  <td className="ltr">{test.validUntil?.slice(0, 10) ?? '—'}</td>
                  <td>
                    {canWrite && test.result === 'PENDING' ? (
                      <div className="table__actions">
                        <button
                          type="button"
                          className="button button--compact"
                          onClick={() => read.mutate({ id: test.id!, result: 'PASSED' })}
                        >
                          {t('clinical.markPassed')}
                        </button>
                        <button
                          type="button"
                          className="button button--ghost button--compact"
                          onClick={() => read.mutate({ id: test.id!, result: 'FAILED' })}
                        >
                          {t('clinical.markFailed')}
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {read.error ? <ErrorState error={read.error} /> : null}

      {canWrite ? (
        recording ? (
          <form
            className="stack"
            onSubmit={(event) => {
              event.preventDefault();
              record.mutate({
                serviceId: serviceId || undefined,
                testedAt: new Date().toISOString(),
              });
            }}
          >
            <label className="field">
              <span className="field__label">{t('clinical.forTreatment')}</span>
              <select
                className="field__input"
                value={serviceId}
                onChange={(event) => setServiceId(event.target.value)}
              >
                <option value="">{t('clinical.anyTreatment')}</option>
                {services.map((service) => (
                  <option key={service.id} value={service.id}>
                    {service.nameEn ?? service.nameAr}
                  </option>
                ))}
              </select>
            </label>
            {record.error ? <ErrorState error={record.error} /> : null}
            <p className="field__hint">{t('clinical.patchTestHint')}</p>
            <div className="table__actions">
              <button type="submit" className="button button--primary" disabled={record.isPending}>
                {t('clinical.recordPatchTest')}
              </button>
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setRecording(false)}
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="button button--compact" onClick={() => setRecording(true)}>
            {t('clinical.recordPatchTest')}
          </button>
        )
      ) : null}
    </>
  );
}

// ── Consents (FR-504, RULE-012) ───────────────────────────────────────────

function Consents({
  clinicId,
  membershipId,
  signatures,
  outstanding,
  canWrite,
  onSign,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly signatures: NonNullable<ClinicalContextResponse['consents']>;
  readonly outstanding: readonly ConsentTemplateResponse[];
  readonly canWrite: boolean;
  readonly onSign: (template: ConsentTemplateResponse) => void;
}) {
  const { t } = useTranslation();
  const client = useApiClient();

  return (
    <>
      {outstanding.length > 0 ? (
        <div className="callout callout--warning">
          <p>{t('clinical.outstandingConsents')}</p>
          <ul className="stack">
            {outstanding.map((template) => (
              <li key={template.id}>
                {template.titleEn ?? template.titleAr}
                {canWrite ? (
                  <button
                    type="button"
                    className="button button--compact"
                    onClick={() => onSign(template)}
                  >
                    {t('clinical.sign')}
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(signatures ?? []).length === 0 ? (
        <p className="field__hint">{t('clinical.noConsents')}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('clinical.consent')}</th>
                <th scope="col">{t('clinical.signedBy')}</th>
                <th scope="col">{t('clinical.signedOn')}</th>
                <th scope="col">{t('common.status')}</th>
                <th scope="col">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(signatures ?? []).map((signature) => (
                <tr key={signature.id}>
                  <th scope="row">
                    {signature.templateCode} <span className="ltr">v{signature.templateVersion}</span>
                  </th>
                  <td>
                    {signature.signerName}
                    {signature.signerRelationship ? ` · ${signature.signerRelationship}` : ''}
                  </td>
                  <td className="ltr">{signature.signedAt ? clinicDay(signature.signedAt) : ''}</td>
                  <td>
                    {signature.withdrawnAt
                      ? t('clinical.withdrawn')
                      : signature.supersededAt
                        ? t('clinical.superseded')
                        : t('clinical.inForce')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="button button--ghost button--compact"
                      onClick={() => {
                        void client
                          .get<string>(
                            `/clinics/${clinicId}/clinical/consents/signatures/${signature.id}/document`,
                          )
                          .then((html) => {
                            // Opened as a document rather than downloaded: the clinic prints it or
                            // files it, and either way it has to render Arabic correctly, which is
                            // why it is HTML in the first place.
                            const view = window.open('', '_blank', 'noopener');
                            view?.document.write(html);
                            view?.document.close();
                          });
                      }}
                    >
                      {t('clinical.viewDocument')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="field__hint">{t('clinical.consentHistoryHint')}</p>
      <span hidden>{membershipId}</span>
    </>
  );
}

function SignConsent({
  clinicId,
  membershipId,
  patientName,
  template,
  onClose,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly patientName: string;
  readonly template: ConsentTemplateResponse;
  readonly onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const arabic = i18n.language.startsWith('ar');
  const [signerKind, setSignerKind] = useState<'PATIENT' | 'GUARDIAN'>('PATIENT');
  const [signerName, setSignerName] = useState(patientName);
  const [relationship, setRelationship] = useState('');
  const [signature, setSignature] = useState<string | null>(null);

  const sign = useApiMutation<unknown, SignConsentRequest>(
    (body) => ({
      path: `/clinics/${clinicId}/clinical/consents/signatures`,
      options: { method: 'POST', body },
    }),
    { invalidates: [['clinics', clinicId, 'clinical', membershipId]], onSuccess: onClose },
  );

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-label={t('clinical.sign')}>
      <div className="modal__panel">
        <header className="modal__header">
          <h2>{arabic ? template.titleAr : template.titleEn}</h2>
          <button type="button" className="button button--ghost button--compact" onClick={onClose}>
            {t('common.close')}
          </button>
        </header>
        <div className="modal__body">
          {/* The words as they will be stored on the signature, in the language being signed in. */}
          <pre className="consent-body">{arabic ? template.bodyAr : template.bodyEn}</pre>

          <div className="form-grid">
            <label className="field">
              <span className="field__label">{t('clinical.signerKind')}</span>
              <select
                className="field__input"
                value={signerKind}
                onChange={(event) => setSignerKind(event.target.value as 'PATIENT' | 'GUARDIAN')}
              >
                <option value="PATIENT">{t('clinical.signerPatient')}</option>
                <option value="GUARDIAN">{t('clinical.signerGuardian')}</option>
              </select>
            </label>
            <label className="field">
              <span className="field__label">{t('clinical.signerName')}</span>
              <input
                className="field__input"
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                required
              />
            </label>
            {signerKind === 'GUARDIAN' ? (
              <label className="field">
                <span className="field__label">{t('clinical.relationship')}</span>
                <input
                  className="field__input"
                  value={relationship}
                  onChange={(event) => setRelationship(event.target.value)}
                  placeholder={t('clinical.relationshipHint')}
                  required
                />
              </label>
            ) : null}
          </div>

          <SignaturePad onChange={setSignature} />
          {sign.error ? <ErrorState error={sign.error} /> : null}
          <p className="field__hint">{t('clinical.guardianHint')}</p>
        </div>
        <footer className="modal__footer">
          <button
            type="button"
            className="button button--primary"
            disabled={sign.isPending || !signature}
            onClick={() =>
              sign.mutate({
                patientMembershipId: membershipId,
                consentTemplateId: template.id!,
                locale: arabic ? 'ar' : 'en',
                signerKind,
                signerName,
                signerRelationship: signerKind === 'GUARDIAN' ? relationship : undefined,
                signatureImage: signature ?? undefined,
              })
            }
          >
            {t('clinical.sign')}
          </button>
        </footer>
      </div>
    </div>
  );
}

// ── Photographs (FR-506, RULE-003) ────────────────────────────────────────

function PhotoSets({
  clinicId,
  membershipId,
  sets,
  canWrite,
}: {
  readonly clinicId: string;
  readonly membershipId: string;
  readonly sets: readonly PhotoSetResponse[];
  readonly canWrite: boolean;
}) {
  const { t } = useTranslation();
  const client = useApiClient();
  const [uploading, setUploading] = useState<string | null>(null);

  const createSet = useApiMutation<PhotoSetResponse, { readonly stage: string }>(
    ({ stage }) => ({
      path: `/clinics/${clinicId}/clinical/photo-sets`,
      options: { method: 'POST', body: { patientMembershipId: membershipId, stage } },
    }),
    { invalidates: [['clinics', clinicId, 'clinical', membershipId]] },
  );

  return (
    <>
      {sets.length === 0 ? <p className="field__hint">{t('clinical.noPhotos')}</p> : null}

      <ul className="stack">
        {sets.map((set) => (
          <li key={set.id} className="callout">
            <strong>{t(`clinical.stages.${set.stage ?? 'BEFORE'}`)}</strong>{' '}
            <span className="ltr">{set.capturedAt ? clinicDay(set.capturedAt) : ''}</span>
            <span className="field__hint">
              {' '}
              · {t('clinical.photoCount', { count: (set.photos ?? []).length })}
            </span>
            {set.releasableForMarketing ? (
              <span className="badge">{t('clinical.marketingReleased')}</span>
            ) : (
              <span className="field__hint"> · {t('clinical.clinicalOnly')}</span>
            )}
            <div className="table__actions">
              {(set.photos ?? []).map((photo, index) => (
                <button
                  key={photo.id}
                  type="button"
                  className="button button--ghost button--compact"
                  onClick={() => {
                    void client
                      .get<PhotoLinkResponse>(
                        `/clinics/${clinicId}/clinical/photo-sets/photos/${photo.id}/link`,
                      )
                      .then((link) => window.open(link.url, '_blank', 'noopener'));
                  }}
                >
                  {t('clinical.openPhoto', { index: index + 1 })}
                </button>
              ))}
              {canWrite ? (
                <label className="button button--compact">
                  {uploading === set.id ? t('clinical.uploading') : t('clinical.addPhoto')}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (!file) {
                        return;
                      }
                      setUploading(set.id!);
                      const form = new FormData();
                      form.append('file', file);
                      void client
                        .upload(`/clinics/${clinicId}/clinical/photo-sets/${set.id}/photos`, form)
                        .finally(() => setUploading(null));
                    }}
                  />
                </label>
              ) : null}
            </div>
          </li>
        ))}
      </ul>

      {canWrite ? (
        <div className="table__actions">
          {(['BEFORE', 'AFTER', 'PROGRESS', 'ADVERSE_EVENT'] as const).map((stage) => (
            <button
              key={stage}
              type="button"
              className="button button--compact"
              onClick={() => createSet.mutate({ stage })}
            >
              {t('clinical.newSet', { stage: t(`clinical.stages.${stage}`) })}
            </button>
          ))}
        </div>
      ) : null}

      {createSet.error ? <ErrorState error={createSet.error} /> : null}
      <p className="field__hint">{t('clinical.photoConsentHint')}</p>
    </>
  );
}
